import { Result } from 'neverthrow';

import type {
  CreateLoadBuildingSuggestionResponse,
  CreateSharepointLoadBuildingSuggestionRequest,
} from 'lib/api/createLoadBuildingSuggestion';
import { buildLocalLoadBuildingSuggestionFromRequest } from 'pages/QuoteView/scrapedSuggestionUtil';
import {
  type Load,
  type NormalizedLoad,
  createInitLoad,
  normalizeLoad,
} from 'types/Load';
import type { ApiError } from 'types/api/ApiError';
import { TMS } from 'types/enums/Integrations';
import type {
  LoadBuildingSuggestions,
  SuggestedLoad,
} from 'types/suggestions/LoadBuildingSuggestions';
import { SuggestionSourceCategories } from 'types/suggestions/QuoteSuggestions';
import captureException from 'utils/captureException';
import { isDateOnlyFormValue } from 'utils/dateOnlyFormValue';

import type {
  SharepointSuggestedLoadPayload,
  SharepointWorkbookSuggestion,
} from '../types/SharepointWorkbookRows';
import { buildSyntheticSharepointLoadBuildingSuggestion } from './sharepointToLoadSuggestion';

export type PersistSharepointGraphSuggestion = (
  request: CreateSharepointLoadBuildingSuggestionRequest
) => Promise<Result<CreateLoadBuildingSuggestionResponse, ApiError>>;

export type ProcessSharepointGraphSuggestionParams = {
  suggestion: SharepointWorkbookSuggestion;
  templateId: string;
  sourceURL: string;
  emailIdForCard: number;
  persist: PersistSharepointGraphSuggestion;
};

function stableSyntheticSuggestionId(sourceExternalID: string): number {
  let hash = 2166136261;
  for (let index = 0; index < sourceExternalID.length; index += 1) {
    hash ^= sourceExternalID.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  hash |= 0;
  if (hash === 0 || hash === -2147483648) {
    return -7201;
  }
  return hash < 0 ? hash : -Math.abs(hash);
}

export function normalizeSharepointGraphSuggestedLoad(
  suggested: SharepointSuggestedLoadPayload
): SuggestedLoad {
  const readyTime = suggested.pickup?.readyTime;
  const mustDeliver = suggested.consignee?.mustDeliver;
  const defaults = createInitLoad();
  const load = {
    ...defaults,
    ...suggested,
    customer: {
      ...defaults.customer,
      ...suggested.customer,
    },
    ...(suggested.billTo
      ? {
          billTo: {
            ...defaults.billTo,
            ...suggested.billTo,
          },
        }
      : {}),
    pickup: {
      ...defaults.pickup,
      ...suggested.pickup,
    },
    consignee: {
      ...defaults.consignee,
      ...suggested.consignee,
    },
    rateData: {
      ...defaults.rateData,
      ...suggested.rateData,
    },
    specifications: {
      ...defaults.specifications,
      ...suggested.specifications,
    },
    additionalReferences:
      suggested.additionalReferences ?? defaults.additionalReferences,
    commodities: suggested.commodities ?? defaults.commodities,
    stops: suggested.stops ?? defaults.stops,
  } as Load;
  const normalized = normalizeLoad(TMS.Turvo, load) as NormalizedLoad;

  return {
    mode: normalized.mode,
    moreThanTwoStops: normalized.moreThanTwoStops,
    batchLoad: normalized.batchLoad,
    poNums: normalized.poNums,
    notes: normalized.notes,
    customer: normalized.customer,
    billTo: normalized.billTo,
    stops: normalized.stops,
    pickup: {
      ...normalized.pickup,
      ...(isDateOnlyFormValue(readyTime) ? { readyTime } : {}),
    },
    consignee: {
      ...normalized.consignee,
      ...(isDateOnlyFormValue(mustDeliver) ? { mustDeliver } : {}),
    },
    rateData: normalized.rateData,
    specifications: normalized.specifications,
    additionalReferences: normalized.additionalReferences,
    commodities: normalized.commodities,
  };
}

function withGraphMetadata(
  card: LoadBuildingSuggestions,
  suggestion: SharepointWorkbookSuggestion,
  templateId: string
): LoadBuildingSuggestions {
  if (!suggestion.writeback?.gridRowIndices.length) {
    return card;
  }
  return {
    ...card,
    sharepointWriteback: {
      templateId,
      gridRowIndices: suggestion.writeback.gridRowIndices,
      targetLabel: suggestion.writeback.targetLabel,
    },
  };
}

export async function processSharepointGraphSuggestion({
  suggestion,
  templateId,
  sourceURL,
  emailIdForCard,
  persist,
}: ProcessSharepointGraphSuggestionParams): Promise<LoadBuildingSuggestions> {
  const suggested = normalizeSharepointGraphSuggestedLoad(suggestion.suggested);
  const persistRequest: CreateSharepointLoadBuildingSuggestionRequest = {
    sourceCategory: SuggestionSourceCategories.Sharepoint,
    source: suggestion.source,
    sourceExternalID: suggestion.sourceExternalID,
    sourceURL,
    suggested,
  };
  const persistResult = await persist(persistRequest);

  if (persistResult.isOk()) {
    const resolvedSuggested = persistResult.value.suggested
      ? normalizeSharepointGraphSuggestedLoad(persistResult.value.suggested)
      : undefined;
    const cardSuggested = resolvedSuggested
      ? {
          ...resolvedSuggested,
          pickup: {
            ...resolvedSuggested.pickup,
            readyTime: suggested.pickup.readyTime,
          },
          consignee: {
            ...resolvedSuggested.consignee,
            mustDeliver: suggested.consignee.mustDeliver,
          },
        }
      : undefined;
    const card = {
      ...buildLocalLoadBuildingSuggestionFromRequest(
        persistRequest,
        persistResult.value.suggestionID,
        cardSuggested
      ),
      emailID: emailIdForCard,
      latestEmailIDFromThread: emailIdForCard,
    };
    return withGraphMetadata(card, suggestion, templateId);
  }

  captureException(new Error('sharepoint graph suggestion persist fail-open'), {
    source: persistRequest.source,
    sourceExternalID: persistRequest.sourceExternalID,
    sourceCategory: persistRequest.sourceCategory,
    message: persistResult.error.message,
  });

  const synthetic = buildSyntheticSharepointLoadBuildingSuggestion(
    suggested,
    emailIdForCard,
    {
      syntheticSuggestionId: stableSyntheticSuggestionId(
        suggestion.sourceExternalID
      ),
      source: suggestion.source,
      sourceExternalID: suggestion.sourceExternalID,
      sourceURL,
      sourceCategory: SuggestionSourceCategories.Sharepoint,
    }
  );
  return withGraphMetadata(synthetic, suggestion, templateId);
}
